import React, { useState } from 'react';
import styled from 'styled-components';

const BLACK_COLOR = '#000000';
const CREAM_COLOR = '#fff';

const Form = styled.form`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  font-size: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${BLACK_COLOR};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${BLACK_COLOR};
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
`;

const Checkbox = styled.input`
  margin-right: 10px;
  width: 18px;
  height: 18px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 14px 28px;
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.secondary ? BLACK_COLOR : CREAM_COLOR};
  background-color: ${props => props.secondary ? CREAM_COLOR : BLACK_COLOR};
  border: 2px solid ${BLACK_COLOR};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.secondary ? CREAM_COLOR : BLACK_COLOR};
    background-color: ${props => props.secondary ? BLACK_COLOR : CREAM_COLOR};
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  padding: 10px 15px;
  margin-bottom: 15px;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 4px;
  font-size: 14px;
`;

const AddressForm = ({ onSubmit, onCancel, initialData = {}, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    street: initialData.street || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip: initialData.zip || '',
    country: initialData.country || 'USA',
    isDefault: initialData.default || false
  });
  
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Basic validation
    if (!formData.name || !formData.street || !formData.city || !formData.state || !formData.zip) {
      setError('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      await onSubmit({
        ...formData,
        default: formData.isDefault
      });
    } catch (err) {
      setError(err.message || 'An error occurred while saving the address');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Form onSubmit={handleSubmit}>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <FormGroup>
        <Label htmlFor="name">Full Name*</Label>
        <Input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="street">Street Address*</Label>
        <Input
          type="text"
          id="street"
          name="street"
          value={formData.street}
          onChange={handleChange}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="city">City*</Label>
        <Input
          type="text"
          id="city"
          name="city"
          value={formData.city}
          onChange={handleChange}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="state">State/Province*</Label>
        <Input
          type="text"
          id="state"
          name="state"
          value={formData.state}
          onChange={handleChange}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="zip">ZIP/Postal Code*</Label>
        <Input
          type="text"
          id="zip"
          name="zip"
          value={formData.zip}
          onChange={handleChange}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="country">Country*</Label>
        <Select
          id="country"
          name="country"
          value={formData.country}
          onChange={handleChange}
          required
        >
          <option value="USA">United States</option>
          <option value="CAN">Canada</option>
          <option value="MEX">Mexico</option>
          <option value="GBR">United Kingdom</option>
          <option value="AUS">Australia</option>
        </Select>
      </FormGroup>
      
      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          id="isDefault"
          name="isDefault"
          checked={formData.isDefault}
          onChange={handleChange}
        />
        <Label htmlFor="isDefault" style={{ display: 'inline', marginBottom: 0 }}>
          Set as default address
        </Label>
      </CheckboxContainer>
      
      <ButtonGroup>
        <Button type="button" secondary onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : isEditing ? 'Update Address' : 'Add Address'}
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default AddressForm;
